import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import './RunningCharacters.css'

type RunnerType = 'default' | 'panic' | 'manuscript' | 'tired' | 'sleeping'

type DeadlineLike = {
  work_title?: string
  episode_number?: string | number
  days_until_submit?: number | null
  status?: string | null
  episode_status?: string | null
  stages_done?: number | null
  done_stages?: number | null
  stages_total?: number | null
  total_stages?: number | null
  stage_summary?: string | null
}

const DONE_STATUSES = new Set(['completed', 'submitted', 'hiatus'])

function isDoneEpisode(ep: DeadlineLike): boolean {
  const status = String(ep?.status ?? ep?.episode_status ?? '').toLowerCase()
  if (DONE_STATUSES.has(status)) return true

  const stagesDone = Number(ep?.stages_done ?? ep?.done_stages ?? NaN)
  const stagesTotal = Number(ep?.stages_total ?? ep?.total_stages ?? 7)
  if (Number.isFinite(stagesDone) && Number.isFinite(stagesTotal) && stagesTotal > 0 && stagesDone >= stagesTotal) return true

  const summary = String(ep?.stage_summary ?? '')
  if (summary) {
    const pairs = summary.split(',').map((pair) => pair.split(':'))
    const known = pairs.filter(([stage, st]) => stage && st)
    if (known.length > 0 && known.every(([, st]) => st === 'done')) return true
  }
  return false
}

function formatDeadlineNote(deadline: DeadlineLike | null): string {
  if (!deadline) return 'NEXT DEADLINE'
  const work = String(deadline.work_title ?? 'DEADLINE').trim()
  const ep = deadline.episode_number != null && deadline.episode_number !== '' ? ` #${deadline.episode_number}` : ''
  const raw = `${work}${ep}`.trim()
  if (raw.length <= 26) return raw.toUpperCase()
  return `${raw.slice(0, 24)}…`.toUpperCase()
}

function useDeadlineNote() {
  const [deadlineNote, setDeadlineNote] = useState('NEXT DEADLINE')

  useEffect(() => {
    let cancelled = false

    api.dashboard.get()
      .then((res: any) => {
        if (cancelled) return
        const deadlines: DeadlineLike[] = Array.isArray(res?.upcoming_deadlines) ? res.upcoming_deadlines : []
        const next = deadlines
          .filter((d) => !isDoneEpisode(d))
          .sort((a, b) => Number(a.days_until_submit ?? 9999) - Number(b.days_until_submit ?? 9999))[0] ?? null
        setDeadlineNote(formatDeadlineNote(next))
      })
      .catch(() => {
        if (!cancelled) setDeadlineNote('NEXT DEADLINE')
      })

    return () => { cancelled = true }
  }, [])

  return deadlineNote
}

function RunningCharacter({ type = 'default' }: { type?: RunnerType }) {
  if (type === 'panic') {
    return (
      <svg viewBox="0 0 100 100" width="40" height="40" className="runner-character-svg">
        <g stroke="var(--runner-black)" strokeWidth="6" strokeLinecap="square" strokeLinejoin="miter">
          <line x1="55" y1="60" x2="70" y2="90" className="runner-leg-b runner-panic-leg" />
          <line x1="50" y1="40" x2="60" y2="10" className="runner-panic-arm-b" />
          <rect x="40" y="32" width="20" height="28" fill="var(--runner-red)" stroke="var(--runner-black)" strokeWidth="4" />
          <polygon points="50,4 64,28 36,28" fill="var(--runner-yellow)" stroke="var(--runner-black)" strokeWidth="4" strokeLinejoin="round" />
          <line x1="45" y1="60" x2="30" y2="90" className="runner-leg-f runner-panic-leg" />
          <line x1="50" y1="40" x2="40" y2="10" className="runner-panic-arm-f" />
        </g>
      </svg>
    )
  }

  if (type === 'manuscript') {
    return (
      <svg viewBox="0 0 100 100" width="40" height="40" className="runner-character-svg">
        <g stroke="var(--runner-black)" strokeWidth="6" strokeLinecap="square" strokeLinejoin="miter">
          <line x1="55" y1="60" x2="70" y2="90" className="runner-leg-b" />
          <line x1="50" y1="40" x2="70" y2="45" />
          <rect x="40" y="32" width="20" height="28" fill="var(--runner-green)" stroke="var(--runner-black)" strokeWidth="4" />
          <circle cx="50" cy="18" r="14" fill="var(--runner-yellow)" stroke="var(--runner-black)" strokeWidth="4" />
          <line x1="45" y1="60" x2="30" y2="90" className="runner-leg-f" />
          <rect x="60" y="25" width="24" height="32" fill="white" stroke="var(--runner-black)" strokeWidth="4" />
          <line x1="66" y1="33" x2="78" y2="33" strokeWidth="2" />
          <line x1="66" y1="41" x2="78" y2="41" strokeWidth="2" />
          <line x1="66" y1="49" x2="78" y2="49" strokeWidth="2" />
          <line x1="50" y1="40" x2="65" y2="45" />
        </g>
      </svg>
    )
  }

  if (type === 'tired') {
    return (
      <svg viewBox="0 0 100 100" width="40" height="40" className="runner-character-svg">
        <g stroke="var(--runner-black)" strokeWidth="6" strokeLinecap="square" strokeLinejoin="miter" transform="rotate(30 50 90)">
          <line x1="55" y1="60" x2="70" y2="90" className="runner-leg-b runner-slow-limb" />
          <line x1="50" y1="40" x2="60" y2="70" className="runner-tired-arm-b" />
          <rect x="40" y="32" width="20" height="28" fill="var(--runner-black)" stroke="var(--runner-black)" strokeWidth="4" />
          <circle cx="50" cy="18" r="14" fill="var(--runner-paper)" stroke="var(--runner-black)" strokeWidth="4" />
          <circle cx="70" cy="10" r="4" fill="var(--runner-blue)" stroke="none" className="runner-pulse" />
          <line x1="45" y1="60" x2="30" y2="90" className="runner-leg-f runner-slow-limb" />
          <line x1="50" y1="40" x2="40" y2="70" className="runner-tired-arm-f" />
        </g>
      </svg>
    )
  }

  if (type === 'sleeping') {
    return (
      <svg viewBox="0 0 110 100" width="58" height="42" className="runner-character-svg">
        <g stroke="var(--runner-black)" strokeWidth="4" strokeLinecap="square" strokeLinejoin="miter">
          {/* 胴体 */}
          <rect x="28" y="68" width="30" height="16" fill="var(--runner-black)" />

          {/* 追加した足：寝落ち後も下半身が見えるようにする */}
          <line x1="30" y1="78" x2="12" y2="88" strokeWidth="6" />
          <line x1="42" y1="82" x2="22" y2="94" strokeWidth="6" />
          <line x1="13" y1="88" x2="8" y2="88" strokeWidth="5" />
          <line x1="23" y1="94" x2="18" y2="94" strokeWidth="5" />

          {/* 頭 */}
          <circle cx="70" cy="76" r="11" fill="var(--runner-paper)" stroke="var(--runner-black)" strokeWidth="4" />

          {/* 倒れた腕 */}
          <line x1="38" y1="76" x2="78" y2="86" />

          <text
            x="75"
            y="53"
            className="runner-sleep-text"
            fontSize="16"
            fill="var(--runner-blue)"
            stroke="none"
            fontWeight="bold"
          >
            Zzz
          </text>
        </g>
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 100 100" width="40" height="40" className="runner-character-svg">
      <g stroke="var(--runner-black)" strokeWidth="6" strokeLinecap="square" strokeLinejoin="miter">
        <line x1="55" y1="60" x2="70" y2="90" className="runner-leg-b" />
        <line x1="50" y1="40" x2="80" y2="50" className="runner-arm-b" />
        <rect x="40" y="32" width="20" height="28" fill="var(--runner-yellow)" stroke="var(--runner-black)" strokeWidth="4" />
        <circle cx="50" cy="18" r="14" fill="var(--runner-blue)" stroke="var(--runner-black)" strokeWidth="4" />
        <line x1="45" y1="60" x2="30" y2="90" className="runner-leg-f" />
        <line x1="50" y1="40" x2="20" y2="50" className="runner-arm-f" />
      </g>
    </svg>
  )
}

export default function RunningCharacters({ className = '', compact = false }: { className?: string; compact?: boolean }) {
  const deadlineNote = useDeadlineNote()
  const dynamicDeadlineNote = useMemo(() => deadlineNote, [deadlineNote])

  return (
    <div className={`runner-strip${compact ? ' compact' : ''} ${className}`} aria-hidden="true">
      <div className="runner-track runner-track-1">
        <div className="runner-label runner-label-light">WHERE IS THE PEN!?</div>
        <RunningCharacter type="panic" />
      </div>

      <div className="runner-track runner-track-2">
        <div className="runner-label runner-label-red">DEADLINE CHASE</div>
        <RunningCharacter type="default" />
        <span className="runner-dot dot-1" />
        <span className="runner-dot dot-2" />
        <span className="runner-dot dot-3" />
        <div className="runner-note runner-deadline-note">{dynamicDeadlineNote}</div>
      </div>

      <div className="runner-track runner-track-3">
        <RunningCharacter type="manuscript" />
        <div className="runner-note runner-note-green">SUBMITTING</div>
      </div>

      <div className="runner-tired-sequence">
        <div className="runner-track-tired">
          <RunningCharacter type="tired" />
          <div className="runner-note runner-note-muted">NEED SLEEP</div>
        </div>

        <div className="runner-track-sleeping">
          <RunningCharacter type="sleeping" />
          <div className="runner-note runner-note-blue">OUT OF BATTERY</div>
        </div>
      </div>
    </div>
  )
}
